import { Box, Button, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import AdminDataGrid from 'csam/admin/components/AdminDataGrid';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AdminPageHeader from 'csam/admin/components/AdminHeader';
import AdminPageLoader from 'csam/admin/components/AdminPageLoader';
import AdminWrapper from 'csam/admin/components/AdminWrapper';
import { getCommonColumns } from 'csam/admin/components/CommonColumns';
import AdminInternalError from 'csam/admin/pages/AdminInternalError';
import { useAuthenticatedMutation, useAuthenticatedQuery } from 'csam/api/api';
import LocaleContext from 'csam/components/LocaleContext';
import { fixed } from 'csam/utils/Constants';
import { useContext, useState, useEffect, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';

interface User {
  email: string;
}

interface FalseLoginData {
  id: number;
  user_id: number;
  user?: User;
  page_name: string;
  created_at: string;
}

interface FalseLoginDataResponse {
  success: boolean;
  data: FalseLoginData[];
  translatableFields: string[];
}

const FalseLoginSsoData = () => {
  const { locale, setLocale } = useContext(LocaleContext);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [baseData, setBaseData] = useState<FalseLoginData[]>([]);

  const deleteMutation = useAuthenticatedMutation<string[], void>(['delete-sso-data'], (ids) => ({
    url: `${fixed}admin/${locale}/user_page_activites`,
    method: 'DELETE',
    data: { ids },
  }));

  const queryClient = useQueryClient();

  const handleDelete = (ids?: string[]) => {
    const idsToDelete = ids || selectedIds;
    deleteMutation.mutate(idsToDelete.map((id) => id.toString()) as unknown as void, {
      onSuccess: () => {
        toast.success(`Record Deleted Successfully`);
        queryClient.invalidateQueries({
          queryKey: ['FalseLoginSsoPage', 'FalseLoginSsoPageData', locale],
        });
      },
    });
  };

  const headers = [
    { label: 'Id', key: 'id' },
    { label: 'User Id', key: 'user_id' },
    { label: 'Email', key: 'email' },
    { label: 'Page Name', key: 'page_name' },
    { label: 'Created At (IST)', key: 'created_at' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      filterable: false,
      width: 40,
      renderCell: (params) => params.value,
    },
    {
      field: 'user_id',
      headerName: 'User ID',
      width: 80,
      renderCell: (params) => params.value,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (params.row.user ? params.row.user.email : 'N/A'),
    },
    { field: 'page_name', headerName: 'Page Name', width: 200 },
    ...getCommonColumns({
      handleDelete,
      showEdit: false,
      showYear: false,
      showCreatedAt: true,
      showDelete: true,
    }),
  ];

  const relations = ['user'];
  const relationsParam = relations.join(',');
  const localeFromStorage = localStorage.getItem('locale') || 'en';
  const { isPending, error, data } = useAuthenticatedQuery<FalseLoginDataResponse>(
    ['FalseLoginSsoPage', 'FalseLoginSsoPageData', locale],
    {
      url: `${fixed}admin/${locale || localeFromStorage}/user_page_activites?relations=${relationsParam}`,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  useEffect(() => {
    if (data?.success) {
      const initialData = data.data.filter(item => item.page_name === 'False login');
      setBaseData(initialData);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    if (!baseData.length) return [];
    if (!activeSearchTerm) return baseData;

    const lowercaseSearchTerm = activeSearchTerm.toLowerCase();

    return baseData.filter(item => {
      if (String(item.id).includes(lowercaseSearchTerm)) {
        return true;
      }
      if (String(item.user_id).includes(lowercaseSearchTerm)) {
        return true;
      }
      const email = item.user?.email;
      if (email && email.toLowerCase().includes(lowercaseSearchTerm)) {
        return true;
      }
      if (item.page_name && item.page_name.toLowerCase().includes(lowercaseSearchTerm)) {
        return true;
      }
      if (item.created_at && item.created_at.toLowerCase().includes(lowercaseSearchTerm)) {
        return true;
      }

      return false;
    });
  }, [baseData, activeSearchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    setActiveSearchTerm(inputValue);
  };

  const handleClear = () => {
    setInputValue('');
    setActiveSearchTerm('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const prepareCSVData = (data: FalseLoginData[]) => {
    return data.map((item) => ({
      id: item.id,
      user_id: item.user_id,
      email: item.user?.email || 'N/A',
      page_name: item.page_name,
      created_at: item.created_at,
    }));
  };

  if (isPending) return <AdminPageLoader />;
  if (error) return <div>Error: {error.message}</div>;
  if (data && !data.success) return <AdminInternalError />;

  return (
    <AdminWrapper>
      <Box className="falseLoginDataAdmin">
        <AdminPageHeader
          title="False Login Page SSO Data"
          locale={locale}
          setLocale={setLocale}
          showLocaleButtonGroup={false}
          handleDelete={handleDelete}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 2
          }}>

            <Button
              type="button"
              sx={{ color: '#fff', lineHeight: '26px' }}
              variant="contained"
              size="small"
              color="success"
              className="download-csv-button"
              title="Download CSV"
            >
              <CSVLink data={prepareCSVData(filteredData)} headers={headers} filename="false_login_sso_data.csv">
                <CloudDownloadIcon sx={{ fontSize: '18px' }} /> CSV
              </CSVLink>
            </Button>
          </Box>
        </AdminPageHeader>
        <Box sx={{ flex: 1, display: 'flex', gap: 1, width: '400px', mb: 2, position: 'relative' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by email, ID..."
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            value={inputValue}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingRight: inputValue ? '40px' : '14px',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {inputValue && (
            <IconButton
              size="small"
              onClick={handleClear}
              title="Clear search"
              sx={{
                position: 'absolute',
                right: '110px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px',
                zIndex: 1,
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
      </Box>
      <AdminDataGrid<FalseLoginData>
        data={{ data: filteredData }}
        columns={columns}
        setSelectedIds={setSelectedIds}
        showCheckbox={true}
      />
    </AdminWrapper>
  );
};

export default FalseLoginSsoData;